import React, { createContext, useContext, useState, useCallback, useRef } from 'react'
import { Check, X } from 'lucide-react'

const FONT = 'Lexend Deca, sans-serif'

interface Toast {
  id: number
  type: 'success' | 'error'
  text: string
  exiting?: boolean
}

interface ToastContextValue {
  showToast: (type: 'success' | 'error', text: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

export const useToast = (): ToastContextValue => {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within a ToastProvider')
  return ctx
}

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([])
  const idRef = useRef(0)

  const showToast = useCallback((type: 'success' | 'error', text: string) => {
    const id = ++idRef.current
    setToasts(prev => [...prev, { id, type, text }])

    // Start exit animation after 2700ms, remove after 3000ms
    setTimeout(() => {
      setToasts(prev => prev.map(t => t.id === id ? { ...t, exiting: true } : t))
    }, 2700)
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id))
    }, 3000)
  }, [])

  const dismissToast = useCallback((id: number) => {
    setToasts(prev => prev.map(t => t.id === id ? { ...t, exiting: true } : t))
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id))
    }, 300)
  }, [])

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}

      {/* Toast container */}
      <div style={{
        position: 'fixed',
        top: '24px',
        right: '24px',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        pointerEvents: 'none'
      }}>
        {toasts.map(toast => (
          <div
            key={toast.id}
            style={{
              padding: '12px 20px',
              borderRadius: '12px',
              background: toast.type === 'success' ? '#dcfce7' : '#fee2e2',
              color: toast.type === 'success' ? '#166534' : '#991b1b',
              fontFamily: FONT,
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              boxShadow: '0 10px 25px -5px rgba(0,0,0,0.15), 0 4px 6px -2px rgba(0,0,0,0.05)',
              border: `1px solid ${toast.type === 'success' ? '#bbf7d0' : '#fecaca'}`,
              pointerEvents: 'auto',
              cursor: 'pointer',
              animation: toast.exiting ? 'toastSlideOut 300ms ease-in forwards' : 'toastSlideIn 300ms ease-out',
              minWidth: '250px',
              maxWidth: '400px'
            }}
            onClick={() => dismissToast(toast.id)}
          >
            {toast.type === 'success'
              ? <Check style={{ height: '18px', width: '18px', marginRight: '10px', flexShrink: 0 }} />
              : <X style={{ height: '18px', width: '18px', marginRight: '10px', flexShrink: 0 }} />
            }
            {toast.text}
          </div>
        ))}
      </div>

      <style>{`
        @keyframes toastSlideIn {
          from { opacity: 0; transform: translateX(100%); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes toastSlideOut {
          from { opacity: 1; transform: translateX(0); }
          to { opacity: 0; transform: translateX(100%); }
        }
      `}</style>
    </ToastContext.Provider>
  )
}
